import StyledDosageString, {
  type StylableDoseProps,
} from "$routes/patient/_lib/styled-dosage-string";
import { makeStyles, Typography } from "@material-ui/core";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles((theme) => ({
  columnWrapper: {
    display: "flex",
    flexDirection: "column",
    padding: 4,
    overflowWrap: "break-word",
  },
  instructionWrapper: {
    marginTop: 2,
    boxShadow: `0 -1.5px ${theme.palette.grey[600]}`,
    paddingBlock: "4mm",
  },
  title: {
    ...theme.typography.subtitle1,
    fontSize: "4mm",
    fontWeight: 700,
    marginBottom: "1mm",
  },
  subtitle: {
    ...theme.typography.subtitle2,
    fontSize: "3.5mm",
    color: theme.palette.grey[600],
    lineHeight: 1.5,
    fontWeight: 700,
  },
  adjacentText: {
    display: "inline",
    marginLeft: 4,
  },
  text: { ...theme.typography.body2, fontSize: "3.5mm", lineHeight: 1.5 },
  instructionDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  instructionLine: {
    display: "flex",
    gap: 8,
  },
}));

interface PrescriptionInstructionProps {
  id: string;
  title: string;
  prescribedAt: Date;
  notes?: string;
  dose: StylableDoseProps;
}

const Instruction: React.FunctionComponent<PrescriptionInstructionProps> = ({
  title,
  prescribedAt,
  notes,
  dose,
}) => {
  const styles = useStyles();
  return (
    <>
      <Typography className={styles.title}>{title}</Typography>
      <div className={styles.instructionDetails}>
        <div className={styles.instructionLine}>
          <Typography className={styles.subtitle}>
            <FormattedMessage
              id="prescriptions.printableInstructions.datePrescribed"
              defaultMessage="Date prescribed"
            />
            {":"}
          </Typography>
          <Typography variant="body2">
            <FormattedMessage
              id="prescriptions.printableInstructions.datePrescribedValue"
              defaultMessage="{prescribedAt, date, short}"
              values={{
                prescribedAt,
              }}
            />
          </Typography>
        </div>
        <div className={styles.instructionLine}>
          <Typography className={styles.subtitle}>
            <FormattedMessage
              id="prescriptions.printableInstructions.taken"
              defaultMessage="Taken"
            />
            {":"}
          </Typography>
          <Typography className={styles.text}>
            <StyledDosageString {...dose} />
          </Typography>
        </div>
        {notes && (
          <>
            <Typography className={styles.subtitle}>
              <FormattedMessage
                id="prescriptions.printableInstructions.notes"
                defaultMessage="Notes"
              />
              {":"}
            </Typography>
            <Typography className={styles.text}>{notes}</Typography>
          </>
        )}
      </div>
    </>
  );
};

interface PrescriptionInstuctionsProps {
  patientName?: string;
  facilityName: string;
  instructions: PrescriptionInstructionProps[];
}

const PrescriptionInstructions: React.FunctionComponent<
  PrescriptionInstuctionsProps
> = ({ instructions, patientName, facilityName }) => {
  const styles = useStyles();
  return (
    <div className={styles.columnWrapper}>
      <Typography className={styles.title}>
        {patientName ? (
          <FormattedMessage
            id="prescriptions.printableInstructions.header"
            defaultMessage="Prescription instructions for {patientName}"
            values={{ patientName }}
          />
        ) : (
          <FormattedMessage
            id="prescriptions.printableInstructions.header.noName"
            defaultMessage="Prescription instructions"
          />
        )}
      </Typography>
      <Typography className={styles.text}>{facilityName}</Typography>
      <ul>
        {instructions
          .filter((instruction) => !!instruction)
          .map((instruction) => (
            <li key={instruction.id} className={styles.instructionWrapper}>
              <Instruction {...instruction} />
            </li>
          ))}
      </ul>
    </div>
  );
};

export default PrescriptionInstructions;
